import React from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BusinessIcon from '@mui/icons-material/Business';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BarChartIcon from '@mui/icons-material/BarChart';
import BuildIcon from '@mui/icons-material/Build';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArticleIcon from '@mui/icons-material/Article';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { labels } from '../constants/labels';

/**
 * Registre des outils de l'application — source unique pour la grille de
 * cartes de `Home.tsx`. Reprend exactement les entrées de `DrawerMenu.tsx`
 * (mêmes routes, mêmes libellés, même gating `multiDeptOnly`) : aucune route
 * ni paramètre de requête nouveau n'est introduit ici.
 */
export interface Tool {
  /** Identifiant stable. */
  key: string;
  label: string;
  /** Chemin react-router. */
  to: string;
  description: string;
  icon: React.ReactNode;
  /** Groupe pour la section « Tous les outils » de l'accueil. */
  group: NavGroup;
  /** true → carte mise en avant dans les « Raccourcis » de l'accueil. */
  primary?: boolean;
  /** true → visible seulement en mode "Tous départements" (miroir DrawerMenu). */
  multiDeptOnly?: boolean;
  /** Code de permission requis pour afficher l'entrée (miroir DrawerMenu).
   *  Confort d'affichage seulement : l'API reste le vrai verrou. */
  permission?: string;
}

export type NavGroup =
  | 'Ventes & documents'
  | 'Tiers'
  | 'Stock & articles'
  | 'Finance'
  | 'Configuration'
  | 'Administration'
  | 'Rapports';

export const NAV_GROUP_ORDER: NavGroup[] = [
  'Ventes & documents',
  'Tiers',
  'Stock & articles',
  'Finance',
  'Configuration',
  'Administration',
  'Rapports',
];

export const TOOLS: Tool[] = [
  // ── Gestion ─────────────────────────────────────────────────
  {
    key: 'pos',
    label: 'Point de vente',
    to: '/pos',
    description: 'Vente rapide au comptoir',
    icon: <PointOfSaleIcon />,
    group: 'Ventes & documents',
    primary: true,
  },
  {
    key: 'facturation',
    label: 'Documents',
    to: '/facturation',
    description: 'Factures, devis, bons de livraison…',
    icon: <ReceiptIcon />,
    group: 'Ventes & documents',
    primary: true,
  },
  {
    key: 'articles',
    label: labels.gestionArticles,
    to: '/article',
    description: 'Catalogue, prix et niveaux de stock',
    icon: <Inventory2Icon />,
    group: 'Stock & articles',
    primary: true,
  },
  {
    key: 'articlesMasse',
    label: 'Actions par famille',
    to: '/article/masse',
    description: 'Prix et famille des articles, modifiés en masse par famille',
    icon: <AutoFixHighIcon />,
    group: 'Stock & articles',
  },
  {
    key: 'comptes',
    label: 'Comptes & Balances',
    to: '/comptes',
    description: 'Soldes et relevés de comptes',
    icon: <AccountBalanceIcon />,
    group: 'Finance',
  },
  {
    key: 'caisse',
    label: labels.gestionCaisse,
    to: '/caisse',
    description: 'Encaissements et décaissements',
    icon: <AccountBalanceWalletIcon />,
    group: 'Finance',
    primary: true,
  },
  {
    key: 'operations-diverses',
    label: 'Opérations diverses',
    to: '/operationsDiverses',
    description: 'Écritures sur comptes tiers',
    icon: <SwapHorizIcon />,
    group: 'Finance',
    primary: true,
  },
  {
    key: 'stock-stat',
    label: 'Stock & Statistiques',
    to: '/StockAndStat',
    description: 'État du stock et mouvements',
    icon: <BarChartIcon />,
    group: 'Stock & articles',
  },
  {
    key: 'immobilisations',
    label: 'Immobilisations',
    to: '/immobilisation',
    description: 'Suivi des immobilisations',
    icon: <BuildIcon />,
    group: 'Stock & articles',
  },

  // ── Tiers ───────────────────────────────────────────────────
  {
    key: 'clients',
    label: labels.clientPluriel,
    to: '/client',
    description: 'Vos clients',
    icon: <PeopleIcon />,
    group: 'Tiers',
    primary: true,
    multiDeptOnly: true,
  },
  {
    key: 'fournisseurs',
    label: labels.fournisseurPluriel,
    to: '/fournisseur',
    description: 'Vos fournisseurs',
    icon: <PersonIcon />,
    group: 'Tiers',
    primary: true,
    multiDeptOnly: true,
  },
  {
    key: 'employes',
    label: labels.employePluriel,
    to: '/employe',
    description: 'Comptes employés',
    icon: <BadgeIcon />,
    group: 'Tiers',
    multiDeptOnly: true,
  },

  // ── Finance ─────────────────────────────────────────────────
  {
    key: 'dashboard',
    label: 'Tableau de bord',
    to: '/dashboard',
    description: "Aperçu de l'activité",
    icon: <DashboardIcon />,
    group: 'Finance',
  },

  // ── Configuration ───────────────────────────────────────────
  {
    key: 'parametres-generiques',
    label: 'Paramètres génériques',
    to: '/parametre_generique',
    description: "Dépôts, caisses, familles, natures de règlement…",
    icon: <SettingsIcon />,
    group: 'Configuration',
    primary: true,
  },
  {
    key: 'options',
    label: 'Options personnalisables',
    to: '/options',
    description: "Réglages personnalisables de l'application",
    icon: <TuneIcon />,
    group: 'Configuration',
    multiDeptOnly: true,
  },
  {
    key: 'ai-tokens',
    label: 'Tokens IA (MCP)',
    to: '/aiTokens',
    description: 'Accès des assistants IA aux données',
    icon: <SmartToyIcon />,
    group: 'Configuration',
    multiDeptOnly: true,
  },
  {
    key: 'departements',
    label: 'Départements',
    to: '/departement',
    description: 'Structure organisationnelle',
    icon: <BusinessIcon />,
    group: 'Configuration',
    multiDeptOnly: true,
  },

  // ── Administration ──────────────────────────────────────────
  {
    key: 'utilisateurs',
    label: 'Utilisateurs',
    to: '/utilisateurs',
    description: 'Comptes de connexion',
    icon: <PeopleIcon />,
    group: 'Administration',
    multiDeptOnly: true,
    permission: 'users.read',
  },
  {
    key: 'roles',
    label: 'Rôles',
    to: '/roles',
    description: 'Profils de permissions',
    icon: <SecurityIcon />,
    group: 'Administration',
    multiDeptOnly: true,
    permission: 'roles.read',
  },
  {
    key: 'user-droits',
    label: 'Droits utilisateurs',
    to: '/user-droits',
    description: 'Permissions et périmètres par utilisateur',
    icon: <AdminPanelSettingsIcon />,
    group: 'Administration',
    multiDeptOnly: true,
    permission: 'roles.read',
  },
  {
    key: 'workflows',
    label: 'Workflows documents',
    to: '/workflows',
    description: 'Circuits de validation',
    icon: <AccountTreeIcon />,
    group: 'Administration',
    multiDeptOnly: true,
  },
  {
    key: 'workflow-statuts',
    label: 'Statuts de documents',
    to: '/workflow-statuts',
    description: 'Étapes et transitions',
    icon: <AccountTreeIcon />,
    group: 'Administration',
    multiDeptOnly: true,
  },

  // ── Rapports ────────────────────────────────────────────────
  {
    key: 'rapports',
    label: 'Générer un rapport',
    to: '/rapports',
    description: 'Exports PDF et Excel',
    icon: <AssessmentIcon />,
    group: 'Rapports',
    multiDeptOnly: true,
  },
  {
    key: 'report-templates',
    label: 'Templates de rapports',
    to: '/reportTemplates',
    description: 'Modèles de mise en page',
    icon: <ArticleIcon />,
    group: 'Rapports',
    multiDeptOnly: true,
  },
];

export const PRIMARY_TOOLS = TOOLS.filter((t) => t.primary);
